<script setup lang="ts">
/**
 * Carte d'une adhésion — rayon /recherche/adhesions, suggestions.
 * Présentation « vitrine » commune (VitrineCarte, Karim 2026-10-12) : prix et
 * durée posés sur l'image.
 */
import { useRechercheHelpers } from "~/composables/useRechercheHelpers";
import { themeAffiche } from "~/utils/themesAffiches";

interface AdhesionCarte {
  slug: string;
  title: string;
  cover_image: string | null;
  price: string;
  duration_type: string;
  duration_days?: number | null;
  season_end_date?: string | null;
  /** Fuseau de l'association (lot 2) — Paris par défaut. */
  timezone?: string | null;
  category: string | null;
  organization_name: string;
  organization_logo: string | null;
  min_price?: number;
  price_count?: number;
}

const props = defineProps<{ plan: AdhesionCarte; priority?: boolean }>();

const { formatCurrency } = useFormatLangue();
const { getDurationLabel } = useRechercheHelpers();

/**
 * « À partir de » (Karim 2026-06-25) : si le groupe contient plusieurs tarifs
 * (adulte/enfant, mensuel/annuel…), on affiche le MIN du groupe. `min_price`
 * vient de l'API publique ; repli sur l'ancien `price`.
 */
const prix = computed(() => {
  const min = props.plan.min_price ?? Number.parseFloat(props.plan.price);
  const montant = Number.isFinite(min) ? min : 0;
  return formatCurrency(montant, { currency: "EUR", decimals: Number.isInteger(montant) ? 0 : 2 });
});
const aPartirDe = computed(() => (props.plan.price_count ?? 1) > 1);
const duree = computed(() => getDurationLabel(props.plan));

/** Thème ; rien pour « Autre » (n'apprend rien). */
const pastille = computed(() =>
  props.plan.category && props.plan.category !== "autre" ? themeAffiche(props.plan.category) : "",
);
</script>

<template>
  <VitrineCarte
    :href="`/a/${plan.slug}`"
    :titre="plan.title"
    :image="plan.cover_image"
    :pastille="pastille"
    :association-nom="plan.organization_name"
    :association-logo="plan.organization_logo"
    :action="$t('commun.vitrine.actionAdhesion')"
    :priority="priority"
  >
    <div class="flex items-baseline justify-between gap-3">
      <p class="min-w-0 truncate text-sm text-white/80">
        <span v-if="aPartirDe" class="mr-1">{{ $t("association.rightColumn.aPartirDe") }}</span>
        <span class="text-lg sm:text-xl text-white" style="font-weight: 700 !important">{{ prix }}</span>
      </p>
      <p v-if="duree" class="shrink-0 max-w-[55%] truncate text-sm text-white/70">
        {{ duree }}
      </p>
    </div>
  </VitrineCarte>
</template>
